import { ArrowLeft, Bell, Building2, History, Loader2, Settings, Shield, User } from 'lucide-react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGetProfileQuery } from '../../services/profileApi';

interface ProfileMenuItem {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
}

const ProfileSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: profile, isLoading } = useGetProfileQuery();

  const menuItems: ProfileMenuItem[] = [
    {
      id: 'overview',
      name: 'My Profile',
      description: 'Personal information & avatar',
      icon: User,
      href: '/profile',
    },
    {
      id: 'security',
      name: 'Security',
      description: 'Password & email settings',
      icon: Shield,
      href: '/profile/security',
    },
    {
      id: 'organizations',
      name: 'Organizations',
      description: 'Projects & teams',
      icon: Building2,
      href: '/profile/organizations',
    },
    {
      id: 'notifications',
      name: 'Notifications',
      description: 'Email & push preferences',
      icon: Bell,
      href: '/profile/notifications',
    },
    {
      id: 'activity',
      name: 'Activity',
      description: 'Recent account activity',
      icon: History,
      href: '/profile/activity',
    },
    {
      id: 'preferences',
      name: 'Preferences',
      description: 'Language, timezone & theme',
      icon: Settings,
      href: '/profile/preferences',
    },
  ];

  const isActive = (href: string) => {
    if (href === '/profile') {
      return location.pathname === '/profile';
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
    }
    if (profile?.name) {
      const parts = profile.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return profile.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (isLoading) {
    return (
      <div className="w-72 bg-white border-r border-gray-200 flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="w-72 bg-white border-r border-gray-200 flex flex-col h-full"
      data-testid="profile-sidebar"
    >
      {/* Sidebar Header with Back Button */}
      <div className="p-3 border-b border-gray-200">
        <button
          onClick={handleBackToDashboard}
          className="flex items-center text-xs text-gray-600 hover:text-brand-primary transition-colors mb-2 group"
        >
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Dashboard
        </button>
        <div className="flex items-center space-x-2.5">
          {/* User Avatar */}
          <div className="w-9 h-9 rounded-full overflow-hidden bg-brand-50 flex items-center justify-center flex-shrink-0">
            {profile?.avatar?.url ? (
              <img
                src={profile.avatar.url}
                alt={profile.name || 'Profile'}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs font-medium text-brand-primary">{getInitials()}</span>
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {profile?.firstName && profile?.lastName
                ? `${profile.firstName} ${profile.lastName}`
                : profile?.name || 'User'}
            </h2>
            <p className="text-xs text-gray-500 truncate">{profile?.email || ''}</p>
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-1">
          {menuItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className={`
                    group flex items-start w-full px-3 py-2.5 text-sm rounded-lg transition-all duration-200
                    ${
                      active
                        ? 'bg-brand-50 border-l-4 border-brand-primary -ml-[2px] pl-[10px]'
                        : 'hover:bg-gray-50'
                    }
                  `}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon
                    className={`
                      h-5 w-5 mr-3 mt-0.5 flex-shrink-0 transition-colors
                      ${active ? 'text-brand-primary' : 'text-gray-400 group-hover:text-brand-primary'}
                    `}
                  />
                  <div className="flex-1 min-w-0">
                    <span
                      className={`font-medium truncate block ${
                        active ? 'text-brand-primary' : 'text-gray-700 group-hover:text-gray-900'
                      }`}
                    >
                      {item.name}
                    </span>
                    <p
                      className={`text-xs mt-0.5 truncate ${
                        active ? 'text-brand-primary/70' : 'text-gray-500'
                      }`}
                    >
                      {item.description}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sidebar Footer - Role Badge */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Account Type</span>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              profile?.role === 'admin'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {profile?.role === 'admin' ? 'Administrator' : 'User'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfileSidebar;
